import useResponsive from "@/app/hooks/useResponsive";
import DisplaySize from "@/app/utils/DisplaySize";
import { BASE_URL } from "@/constant/apiPath";
import { useAuth } from "@/context/AuthContext";
import { useLanguageContext } from "@/context/LanguageContext";
import { formatThousands } from "@/helpers";
import useMyQuery from "@/hooks/useMyQuery";
import type { TokenData } from "@/models/token";
import { SettingOutlined } from "@ant-design/icons";
import { Skeleton, Typography } from "antd";
import classNames from "classnames";
import type { FC } from "react";
import { memo, useMemo, useState } from "react";
import PriceChange from "../PriceChange";
import WatchlistSettingModal from "./WatchlistQuickSettingModal";
import { useWatchlist } from "@/context/WatchlistContext";
import { usePrice } from "@/context/PriceContext";
import { useCurrencyContext } from "@/context/CurrencyContext";

interface TokenCarouselResponse {
  data: TokenData[];
}

const TokenCarousel: FC = () => {
  const screenSize = useResponsive();
  const isMobile = screenSize < DisplaySize.Tablet;
  const { language } = useLanguageContext();
  const { authState, openLoginModal } = useAuth();
  const [isWatchlistModalOpen, setIsWatchlistModalOpen] = useState(false);
  const { selectedWatchlist, sections } = useWatchlist();
  const { getTokenById } = usePrice();
  const { currentCurrency } = useCurrencyContext();

  // Get all tokens from selected watchlist's sections
  const watchlistTokens = useMemo(() => {
    if (!selectedWatchlist || !sections) return [];
    return sections
      .filter(section => section.section.watchlist_id === selectedWatchlist)
      .flatMap(section => section.tokens || [])
      .filter(Boolean)
      .map(tokenId => getTokenById(tokenId as any))
      .filter((token): token is TokenData => Boolean(token));
  }, [selectedWatchlist, sections, getTokenById]);

  const { data, isLoading } = useMyQuery<TokenCarouselResponse>({
    url: `/v1/market-movement/token-info/public/trending`,
    params: { limit: 20 },
    includeParamsInQueryKey: true,
  });

  const displayTokens = selectedWatchlist && watchlistTokens.length > 0 ? watchlistTokens : data?.data || [];

  const handleSettingsClick = () => {
    if (authState === "logged") {
      setIsWatchlistModalOpen(true);
    } else {
      openLoginModal({
        title: "Custom market overview",
        message: "You want to set up market overview? Please log in!",
      });
    }
  };

  if (isLoading && !watchlistTokens.length) {
    return (
      <div className="flex items-center justify-between">
        <div className="w-[90vw] overflow-hidden">
          <div className="flex gap-2 p-1 overflow-hidden">
            {[...Array(10)].map((_, index) => (
              <div key={index} className="flex items-center gap-2 py-1">
                <Skeleton.Button active size="small" style={{ width: 60 }} />
                <Skeleton.Button active size="small" style={{ width: 80 }} />
                <Skeleton.Button active size="small" style={{ width: 40 }} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end min-w-[10vw]">
          <button className="bg-transparent border-none cursor-pointer hover:bg-gray-100 p-2 rounded-full" disabled>
            <SettingOutlined className="text-gray-300 text-lg" />
          </button>
        </div>
      </div>
    );
  }

  const renderTokenItem = (item: TokenData, index: number, array: TokenData[], key: string) => (
    <div
      key={`${item.symbol}-${key}-${index}`}
      className={classNames("inline-flex items-center gap-1 rounded-md px-3 py-1 mx-1 cursor-pointer relative")}
      onClick={() => window.open(`${BASE_URL}/${language}/markets/${item.symbol}`, "_blank")}
    >
      <Typography.Text>{item.symbol}</Typography.Text>
      <Typography.Text>{formatThousands(item.price, currentCurrency)}</Typography.Text>
      <PriceChange price={item.price_change?.change_1D?.percentage ?? 0} />
      {index !== array.length - 1 && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-[1px] bg-gray-300" />
      )}
    </div>
  );

  return (
    <div className="w-full overflow-hidden relative">
      <div className="flex items-center justify-between">
        <div className={`${isMobile ? "w-[85vw]" : "w-[90vw]"} overflow-hidden`}>
          <div className="whitespace-nowrap inline-block marquee">
            {/* First set of tokens */}
            {displayTokens.map((item, index, array) => renderTokenItem(item, index, array, "1"))}

            {/* Duplicate set for seamless scrolling */}
            {displayTokens.map((item, index, array) => renderTokenItem(item, index, array, "2"))}
          </div>
        </div>
        <div className={`flex items-center justify-end ${isMobile ? "min-w-[15vw]" : "min-w-[10vw]"}`}>
          <button
            className="bg-transparent border-none cursor-pointer hover:bg-gray-100 p-2 rounded-full"
            onClick={handleSettingsClick}
            aria-label="Market settings"
          >
            <SettingOutlined className={`${isMobile ? "text-base" : "text-lg"} text-gray-500`} />
          </button>
        </div>
      </div>

      <WatchlistSettingModal open={isWatchlistModalOpen} onClose={() => setIsWatchlistModalOpen(false)} />

      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .marquee {
          animation: marquee 500s linear infinite;
        }

        .marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default memo(TokenCarousel);
